import Link from "next/link";

export type ActionButtonVariant = "yellow" | "white";

interface ActionButtonProps {
  href: string;
  children: React.ReactNode;
  variant?: ActionButtonVariant;
}

const VARIANT_CLASSES: Record<ActionButtonVariant, { face: string; shadow: string }> = {
  yellow: { face: "bg-cta-yellow hover:bg-cta-yellow-light", shadow: "bg-cta-yellow-shadow" },
  white: { face: "bg-white hover:bg-surface-muted-hover", shadow: "bg-surface-muted-shadow" },
};

// Helper function to check if href is external
function isExternalLink(href: string): boolean {
  // Empty or invalid href
  if (!href || typeof href !== "string") {
    return false;
  }

  // Protocol-relative URLs (//example.com) are external
  if (href.startsWith("//")) {
    return true;
  }

  // Absolute paths starting with / are internal
  if (href.startsWith("/")) {
    return false;
  }

  // Check for protocol schemes
  const protocolMatch = href.match(/^([a-zA-Z][a-zA-Z\d+\-.]*):/);
  if (!protocolMatch) {
    // No protocol found - relative path, treat as internal
    return false;
  }

  const protocol = protocolMatch[1].toLowerCase();

  // Common external protocols
  const externalProtocols = ["http", "https"];
  if (externalProtocols.includes(protocol)) {
    return true;
  }

  // Special protocols (mailto, tel, data, javascript, etc.)
  // For security, treat these as external so they don't get Next.js Link behavior
  // This prevents potential XSS issues with javascript: URLs
  const specialProtocols = ["mailto", "tel", "sms", "data", "javascript", "blob", "file"];
  if (specialProtocols.includes(protocol)) {
    return true;
  }

  // Unknown protocols - treat as external for safety
  return true;
}

export function ActionButton({ href, children, variant = "yellow" }: ActionButtonProps) {
  const isExternal = isExternalLink(href);
  const linkProps = isExternal ? { target: "_blank" as const, rel: "noopener noreferrer" } : {};
  const colors = VARIANT_CLASSES[variant];

  return (
    <Link href={href} {...linkProps} className="relative inline-block" data-variant={variant}>
      {/* Shadow layer behind - 6px taller, top-aligned */}
      <div
        className={`absolute top-0 left-0 w-full h-[66px] ${colors.shadow} rounded-[30px] min-w-[240px] z-0`}
        style={{ boxShadow: "0 0 0 4px black" }}
      />
      {/* Main button */}
      <div
        className={`relative ${colors.face} h-[60px] rounded-[30px] min-w-[240px] flex items-center justify-center text-ink-900 font-semibold charlie-kingdom-font text-2xl transition-colors z-10`}
      >
        {children}
      </div>
    </Link>
  );
}
